import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ArrowLeft, Search, Check, X, Clock } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

export function SmartCorrectSuggestionsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }

  const { data: service } = useQuery({
    queryKey: ['smart-correct', id],
    queryFn: () =>
      api
        .get('/api/smart-correct/get', { params: { service_instance_id: id } })
        .then((r) => r.data),
    enabled: !!id,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['smart-correct-suggestions', id, page, debouncedSearch],
    queryFn: () =>
      api
        .get(`/api/smart-correct/${id}/suggestions`, {
          params: {
            offset: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        })
        .then((r) => r.data),
    enabled: !!id,
  });

  const acceptMutation = useMutation({
    mutationFn: (suggestionId: string) =>
      api.patch(`/api/smart-correct/${id}/suggestions/${suggestionId}`, {
        action: 'accept',
      }),
    onSuccess: () => {
      toast.success('Suggestion accepted — vendor confirmed');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-suggestions', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-vendor-stats', id],
      });
    },
    onError: () => toast.error('Failed to accept suggestion'),
  });

  const rejectMutation = useMutation({
    mutationFn: (suggestionId: string) =>
      api.patch(`/api/smart-correct/${id}/suggestions/${suggestionId}`, {
        action: 'reject',
      }),
    onSuccess: () => {
      toast.success('Suggestion rejected');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-suggestions', id],
      });
    },
    onError: () => toast.error('Failed to reject suggestion'),
  });

  const acceptAllMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/smart-correct/${id}/suggestions/accept-all`, {
        minConfidence: 70,
      }),
    onSuccess: () => {
      toast.success('All high-confidence suggestions accepted');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-suggestions', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-vendor-stats', id],
      });
    },
    onError: () => toast.error('Failed to accept suggestions'),
  });

  const s = service?.query?.[0] ?? service;
  const suggestions = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/smart-correct/${id}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-yellow-500" />
            Pending Suggestions
          </h1>
          <p className="text-gray-500 text-sm">
            {s?.service_title ?? 'Smart Correct'} — Review vendor suggestions
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => acceptAllMutation.mutate()}
          disabled={acceptAllMutation.isPending}
        >
          <Check className="h-4 w-4 mr-2" />
          {acceptAllMutation.isPending
            ? 'Accepting...'
            : 'Accept All (>70%)'}
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search suggestions..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10 max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">
          Loading suggestions...
        </div>
      ) : suggestions.length === 0 ? (
        <Card className="p-12 text-center">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            No pending suggestions
          </h3>
          <p className="text-gray-500">
            New suggestions will appear here when Smart Correct finds potential
            vendor matches that need review.
          </p>
        </Card>
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Extracted Name</TableHead>
                  <TableHead>Matched Vendor</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Transaction</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((sug: any) => (
                  <TableRow key={sug.id || sug.suggestion_id}>
                    <TableCell className="font-medium">
                      {sug.extracted_name}
                    </TableCell>
                    <TableCell>{sug.matched_vendor_name || 'No match'}</TableCell>
                    <TableCell>
                      {sug.confidence !== undefined &&
                      sug.confidence !== null ? (
                        <Badge
                          variant={
                            sug.confidence >= 80
                              ? 'success'
                              : sug.confidence >= 60
                              ? 'secondary'
                              : 'destructive'
                          }
                        >
                          {sug.confidence}%
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {sug.transaction_id?.slice(0, 8) || '—'}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(sug.created || sug.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Accept"
                          onClick={() =>
                            acceptMutation.mutate(
                              sug.id || sug.suggestion_id
                            )
                          }
                          disabled={acceptMutation.isPending}
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Reject"
                          onClick={() =>
                            rejectMutation.mutate(
                              sug.id || sug.suggestion_id
                            )
                          }
                          disabled={rejectMutation.isPending}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}